<template>
  <Teleport to="body">
    <Transition name="ras-popup-fade">
      <div
        v-if="showRasPopUp"
        class="ras-popup-backdrop"
        aria-hidden="true"></div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="ras-popup-fade">
      <div
        v-if="showRasPopUp"
        class="ras-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ras-popup-title"
        @click.self="closeRasPopUp">
        <div
          ref="popupContent"
          data-lenis-prevent
          tabindex="-1"
          class="ras-popup-content">
          <button
            class="ras-popup-close"
            type="button"
            aria-label="Close popup"
            @click="closeRasPopUp">
            <span></span>
            <span></span>
          </button>

          <div class="ras-popup-scroll-body">
            <h2
              v-if="!submitted && popupOptions.title"
              id="ras-popup-title"
              v-html="popupOptions.title"></h2>

            <div
              v-if="!submitted"
              class="ras-popup-divider"
              aria-hidden="true">
              <span></span>
              <span></span>
            </div>

            <div
              v-if="!submitted && popupOptions.topText"
              class="ras-popup-top-text"
              v-html="popupOptions.topText"></div>

            <template v-if="!submitted">
              <div
                v-if="isLoading"
                class="ras-popup-status"
                role="status">
                Loading form…
              </div>

              <FormKit
                v-else-if="form"
                id="ras-gravity-form"
                type="form"
                :actions="false"
                :incomplete-message="false"
                @submit="submitHandler"
                @submit-invalid="showErrors">
                <FormKitSchema :schema="formSchema" />
              </FormKit>

              <div
                v-else-if="loadError"
                class="ras-popup-status ras-popup-error"
                role="alert">
                {{ loadError }}
              </div>

              <div
                v-if="messages.length"
                class="ras-popup-response error"
                role="alert"
                v-html="messages.join('<br>')"></div>

              <a
                v-if="!isLoading && !form && popupOptions.button?.url"
                class="ras-popup-link"
                :href="popupOptions.button.url"
                :target="popupOptions.button.target || '_self'"
                :rel="
                  popupOptions.button.target === '_blank'
                    ? 'noopener noreferrer'
                    : undefined
                ">
                {{ popupOptions.button.title }}
              </a>
            </template>

            <h2
              v-else
              id="ras-popup-title"
              class="ras-popup-thank-you"
              role="status">
              {{ thankYouMessage }}
            </h2>

            <div
              v-if="popupOptions.bottomText"
              class="ras-popup-bottom-text"
              v-html="popupOptions.bottomText"></div>

            <p
              v-if="popupOptions.footerText"
              class="ras-popup-footer-text">
              {{ popupOptions.footerText }}
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { FormKitSchema } from '@formkit/vue'
import { reset } from '@formkit/core'
import Axios from 'axios'
import { useStore } from '@/stores/main'
import useSmoothScroll from '@/components/composables/useSmoothScroll'

const store = useStore()
const route = useRoute()
const { locoScroll } = useSmoothScroll()
const showRasPopUp = ref(false)
const popupContent = ref(null)
const form = ref(null)
const isLoading = ref(false)
const isSubmitting = ref(false)
const submitted = ref(false)
const loadError = ref('')
const messages = ref([])
let openTimer = null
let closeTimer = null

const thankYouMessage =
  'Thank you for sharing your words of encouragement! Your message is now part of this shared expression of hope and support.'

const siteUrl =
  window.__VUE_WORDPRESS__?.state?.site?.url || window.location.origin

const popupOptions = computed(() => {
  const options = store.acfOptions() || {}

  return {
    title: options.ras_popup_title || '',
    topText: options.ras_popup_top_text || '',
    formId: options.ras_popup_form_id || '',
    button: options.ras_popup_button || null,
    bottomText: options.ras_popup_bottom_text || '',
    footerText: options.ras_popup_footer_text || '',
  }
})

const matchesCampaignUrl = computed(() => {
  const normalizedPath = route.path.replace(/\/+$/, '') || '/'
  const expectedQuery = {
    utm_source: 'RAS',
    utm_medium: 'QR',
    utm_campaign: 'Wall',
    utm_content: 'RVMD',
  }

  return (
    normalizedPath === '/revolution-ras' &&
    Object.entries(expectedQuery).every(
      ([key, value]) => route.query[key] === value
    )
  )
})

const gfInputName = (inputId) => `input_${String(inputId).replace('.', '_')}`

const selectOptions = (choices = []) =>
  choices.map((choice) => ({
    label: choice.text,
    value: choice.value ?? choice.text,
  }))

const validationTypes = (field) => {
  const validation = []

  if (field.isRequired) validation.push('required')
  if (field.type === 'email') validation.push('email')
  if (field.type === 'textarea' && Number(field.maxLength) > 0) {
    validation.push(`length:0,${field.maxLength}`)
  }

  return validation.join('|')
}

const fieldClass = (field, extraClass = '') =>
  [field.cssClass, field.isRequired ? 'required' : '', extraClass]
    .filter(Boolean)
    .join(' ')

const formSchema = computed(() => {
  const choiceFields = new Set(['select', 'radio', 'checkbox', 'multiselect'])
  const fields = (form.value?.fields || [])
    .filter((field) => field.type !== 'submit')
    .map((field) => {
      if (field.type === 'html') {
        return {
          $el: 'div',
          attrs: {
            class: fieldClass(field, 'ras-form-html'),
            innerHTML: field.content || '',
          },
        }
      }

      if (field.type === 'consent') {
        const checkboxLabel = field.checkboxLabel || field.label
        const inputs = Array.isArray(field.inputs) ? field.inputs : []
        const inputName = (number) => {
          const input = inputs.find(
            (item) => String(item.id).split('.').pop() === String(number)
          )

          return gfInputName(input?.id ?? `${field.id}.${number}`)
        }

        return {
          $el: 'div',
          attrs: { class: fieldClass(field, 'ras-consent-field') },
          children: [
            field.description
              ? {
                  $el: 'div',
                  attrs: {
                    class: 'ras-consent-description',
                    innerHTML: field.description,
                  },
                }
              : null,
            {
              $formkit: 'checkbox',
              name: inputName(1),
              label: checkboxLabel,
              onValue: '1',
              offValue: '',
              validation: validationTypes(field),
            },
            {
              $formkit: 'hidden',
              name: inputName(2),
              value: checkboxLabel,
            },
            {
              $formkit: 'hidden',
              name: inputName(3),
              value: form.value?.latestFormRevisionId || '',
            },
          ].filter(Boolean),
        }
      }

      const type = field.type === 'phone' ? 'text' : field.type
      const schema = {
        $formkit: type,
        name: gfInputName(field.id),
        label: field.label,
        help: field.description || undefined,
        placeholder: field.placeholder,
        maxlength: field.maxLength || undefined,
        validation: validationTypes(field),
        classes: {
          outer: fieldClass(field),
        },
      }

      if (choiceFields.has(type) && field.choices) {
        schema.options = selectOptions(field.choices)
      }

      return schema
    })

  return [
    {
      $el: 'div',
      attrs: { class: 'ras-popup-fields' },
      children: fields,
    },
    {
      $formkit: 'submit',
      label:
        popupOptions.value.button?.title ||
        form.value?.button?.text ||
        'Submit my message',
      disabled: isSubmitting.value,
      classes: {
        outer: 'ras-submit-outer',
        input: 'ras-submit-button',
      },
    },
  ]
})

const getForm = async () => {
  if (!popupOptions.value.formId || form.value) return

  isLoading.value = true
  loadError.value = ''

  try {
    const response = await Axios.get(
      `${siteUrl}/wp-json/wp/v2/forms/${popupOptions.value.formId}`
    )

    if (!response.data?.id) {
      throw new Error('The selected Gravity Form could not be found.')
    }

    form.value = response.data
  } catch (error) {
    loadError.value =
      error.response?.data?.message ||
      error.message ||
      'The form could not be loaded. Please try again later.'
  } finally {
    isLoading.value = false
  }
}

const submissionErrors = (response) =>
  Object.values(response?.validation_messages || {})
    .flatMap((message) => (Array.isArray(message) ? message : [message]))
    .filter(Boolean)

const submitHandler = async (data) => {
  if (isSubmitting.value || !form.value?.submitURL) return

  isSubmitting.value = true
  messages.value = []

  try {
    const response = await Axios.post(form.value.submitURL, data)
    const result = response.data || {}

    if (!result.is_valid) {
      messages.value = submissionErrors(result)

      if (!messages.value.length) {
        messages.value = ['Please review the form and try again.']
      }

      return
    }

    messages.value = []
    reset('ras-gravity-form')
    submitted.value = true
    closeTimer = window.setTimeout(closeRasPopUp, 4000)
  } catch (error) {
    messages.value = [
      error.response?.data?.message ||
        'The form could not be submitted. Please try again later.',
    ]
  } finally {
    isSubmitting.value = false
  }
}

const showErrors = () => {
  messages.value = ['Please complete all required fields.']
}

const openRasPopUp = async () => {
  if (
    !matchesCampaignUrl.value ||
    window.sessionStorage.getItem('rasCampaignPopUpShown') === 'true'
  ) {
    return
  }

  window.sessionStorage.setItem('rasCampaignPopUpShown', 'true')
  showRasPopUp.value = true
  await getForm()
}

const closeRasPopUp = () => {
  window.clearTimeout(closeTimer)
  showRasPopUp.value = false
}

const scheduleOpen = (delay = 300) => {
  window.clearTimeout(openTimer)

  if (!matchesCampaignUrl.value) {
    closeRasPopUp()
    return
  }

  openTimer = window.setTimeout(openRasPopUp, delay)
}

const handleKeydown = (event) => {
  if (event.key === 'Escape' && showRasPopUp.value) closeRasPopUp()
}

watch(showRasPopUp, (value) => {
  if (value) {
    locoScroll.value?.stop?.()
    nextTick(() => popupContent.value?.focus())
    return
  }

  locoScroll.value?.start?.()
})

watch(
  () => route.fullPath,
  () => scheduleOpen(300)
)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  const introAlreadyShown =
    window.sessionStorage.getItem('introShown') === 'true'

  scheduleOpen(introAlreadyShown ? 300 : 2000)
})

onBeforeUnmount(() => {
  window.clearTimeout(openTimer)
  window.clearTimeout(closeTimer)
  window.removeEventListener('keydown', handleKeydown)
  locoScroll.value?.start?.()
})
</script>

<style lang="scss">
.ras-popup-backdrop {
  @apply fixed inset-0 z-[102] bg-black/80;
}

.ras-popup {
  @apply fixed inset-0 z-[103] flex items-center justify-center p-5;

  .ras-popup-content {
    @apply relative max-h-screen w-full max-w-[920px] overflow-y-auto bg-balanced outline-none md:max-h-[92vh];
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .ras-popup-scroll-body {
    @apply flex min-h-full w-full flex-col px-7 pb-5 pt-[54px] md:px-10 md:pb-6 md:pt-[56px];
  }

  .ras-popup-close {
    @apply absolute right-6 top-5 z-2 flex h-10 w-10 cursor-pointer appearance-none items-center justify-center border-0 bg-transparent p-0 shadow-none md:right-8 md:top-14;

    span {
      @apply absolute block h-[3px] w-[34px] bg-approachable;

      &:first-child {
        @apply rotate-45;
      }

      &:last-child {
        @apply -rotate-45;
      }
    }

    &:focus-visible {
      @apply outline outline-2 outline-offset-4 outline-approachable;
    }
  }

  h2 {
    @apply m-0 max-w-[calc(100%_-_50px)] font-FKDisplay text-[36px] font-normal leading-[40px] tracking-[-0.72px] text-progressive md:text-[42px] md:leading-[46px] md:tracking-[-0.84px];
  }

  .ras-popup-thank-you {
    @apply max-w-[calc(100%_-_50px)] text-[25px] leading-[28px] tracking-[-0.5px] md:text-[29px] md:leading-[32px] md:tracking-[-0.59px];
  }

  .ras-popup-divider {
    @apply mb-5 mt-4 flex h-[3px] w-[100px] overflow-hidden md:mb-5 md:w-[158px];

    span:first-child {
      @apply w-1/2 bg-approachable;
    }

    span:last-child {
      @apply w-1/2 bg-progressive;
    }
  }

  .ras-popup-top-text,
  .ras-popup-response,
  .ras-popup-status {
    @apply text-white;

    &,
    p,
    a,
    strong,
    em {
      @apply font-FKGrotesk text-[13px] font-normal leading-[18px] text-white md:text-[18px] md:leading-[22px];
    }

    p:last-child {
      @apply mb-0;
    }

    a {
      @apply underline;
    }
  }

  .ras-popup-bottom-text {
    * {
      @apply font-FKGrotesk text-[11px] font-normal leading-[1.2] text-white md:text-[16px];
    }

    a {
      @apply underline;
    }
  }

  .ras-popup-top-text {
    @apply mb-6;
  }

  .ras-popup-bottom-text {
    @apply mt-4 md:mt-5;

    &,
    p,
    a,
    strong,
    em {
      @apply text-[13px] leading-[15px] md:text-[16px] md:leading-[1.2];
    }
  }

  .ras-popup-footer-text {
    @apply mb-0 mt-2 w-full text-right font-FKGrotesk text-[10px] font-normal leading-[12px] text-white md:text-[11px] md:leading-[13px];
  }

  .formkit-form {
    @apply flex flex-col;
  }

  .ras-popup-fields {
    @apply w-full;
  }

  .formkit-outer {
    @apply mb-4 w-full;
  }

  .formkit-label,
  .formkit-legend {
    @apply sr-only;
  }

  .hidden_label .formkit-label,
  .hidden-label .formkit-label {
    @apply sr-only;
  }

  .required > .formkit-wrapper > .formkit-label,
  .required > .formkit-fieldset > .formkit-legend {
    &::after {
      content: '*';
      @apply ml-1 text-progressive;
    }
  }

  .formkit-help {
    @apply mb-2 font-FKGrotesk text-[13px] leading-[16px] text-white/80;
  }

  input:not([type='checkbox']):not([type='radio']),
  textarea,
  select {
    @apply w-full rounded-[15px] border-0 bg-white px-5 py-4 font-FKGrotesk text-[18px] leading-[22px] text-humble shadow-none outline-none placeholder:text-humble/50 focus:ring-2 focus:ring-approachable;
  }

  textarea {
    @apply min-h-[115px] resize-y md:min-h-[208px];
  }

  .formkit-fieldset {
    @apply m-0 border-0 p-0;
  }

  .formkit-options {
    @apply flex flex-col gap-2;
  }

  .formkit-option .formkit-wrapper,
  .ras-consent-field .formkit-wrapper {
    @apply flex items-start gap-3;
  }

  input[type='checkbox'],
  input[type='radio'] {
    @apply mt-1 h-5 w-5 flex-none border-white bg-transparent text-progressive focus:ring-approachable;
  }

  .ras-consent-description,
  .ras-form-html {
    @apply mb-3 font-FKGrotesk text-[13px] leading-[16px] text-white;

    * {
      @apply text-white;
    }
  }

  .formkit-messages {
    @apply mt-2 pl-0;

    .formkit-message {
      @apply font-FKGrotesk text-[13px] leading-[16px] text-white;
    }
  }

  .ras-submit-outer {
    @apply m-0 w-fit;
  }

  .ras-submit-button,
  .ras-popup-link {
    @apply inline-flex min-h-[40px] w-fit cursor-pointer items-center justify-center rounded-full border-0 bg-progressive px-7 py-2 font-FKGrotesk text-[16px] font-normal leading-[20px] text-humble transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-approachable disabled:cursor-not-allowed disabled:opacity-50 md:min-h-[52px] md:px-8 md:py-3 md:text-[18px] md:leading-[22px];
  }

  .ras-popup-response {
    @apply mb-3 rounded-[15px] bg-white p-5;

    &.error {
      @apply border border-white/50;
    }
  }

  .ras-popup-error {
    @apply mb-4;
  }
}

.ras-popup-fade-enter-active,
.ras-popup-fade-leave-active {
  transition: opacity 250ms ease;
}

.ras-popup-fade-enter-from,
.ras-popup-fade-leave-to {
  opacity: 0;
}
</style>
